import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

import { useSearchInputManager } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import { RecentSearchListItem, useRecentSearch } from '@/src/hooks/srp/recentSearch'
import { isServer } from '@/src/utils/browser'
import { getFormattedDate } from '@/src/utils/date'
import type { HomeSearchPrefillDetail } from '@/src/utils/navigation/useHomeSearchPrefill'
import { useLocale } from 'next-intl'

/**
 * Generates a random 12-character alphanumeric string
 * @returns Random string of 12 characters
 */
const randomString = (): string => {
    let text = ''
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    for (let i = 0; i < 12; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length))
    }
    return text
}

/**
 * Custom hook that extends the existing useSearchInputManager for home page specific behavior
 * Reuses all existing logic and only overrides the search behavior to navigate to /flights/results
 * @param maxSegments - Maximum number of segments allowed (default: 6 for desktop, 3 for mobile)
 */
export const useHomePageSearchManager = ({
    maxSegments,
    serverRecentSearches,
    fromParam,
    toParam
}: {
    maxSegments: number
    serverRecentSearches: RecentSearchListItem[]
    fromParam?: any[] | null
    toParam?: any[] | null
}) => {
    const locale = useLocale()
    const router = useRouter()
    const { getRecentSearchList } = useRecentSearch()

    let searchForPrefill = {
        from: '',
        to: '',
        origin: '',
        destination: '',
        depart_date: '',
        return_date: ''
    }

    // Priority: urlPrefill > recentSearch
    if (fromParam || toParam) {
        // Use URL params prefill (from/to from URL)
        const from = fromParam?.[0] ?? null
        const to = toParam?.[0] ?? null
        
        const buildLocation = (data: any) =>
          data?.payload?.cityName
            ? `${data.payload.airportCode} - ${data.payload.cityName}`
            : data?.payload?.airportCode ?? ''
        
        searchForPrefill = {
          from: from?.payload?.airportCode ?? '',
          to: to?.payload?.airportCode ?? '',
          origin: from ? buildLocation(from) : '',
          destination: to ? buildLocation(to) : '',
          depart_date: '',
          return_date: ''
        }
    } else {
        // Fall back to recent search
        const recentSearches = isServer() ? serverRecentSearches : getRecentSearchList()
        const lastSearch = recentSearches?.[0]
        if (lastSearch) {
            // Convert to format expected by useSearchInputManager (includes dates)
            searchForPrefill = {
                from: lastSearch.from,
                to: lastSearch.to,
                origin: lastSearch.fromCity ? `${lastSearch.from} - ${lastSearch.fromCity}` : lastSearch.from,
                destination: lastSearch.toCity ? `${lastSearch.to} - ${lastSearch.toCity}` : lastSearch.to,
                depart_date: lastSearch.departDate,
                return_date: lastSearch.returnDate || ''
            }
        }
    }

    const searchManager = useSearchInputManager(undefined, undefined, searchForPrefill, maxSegments)

    // Listen for homepage prefill events dispatched from other homepage widgets
    useEffect(() => {
        const listener = (event: Event) => {
            const { detail } = event as CustomEvent<HomeSearchPrefillDetail>
            if (!detail) return

            if (detail.from?.code) {
                const display = detail.from.display || detail.from.code
                searchManager.onAirportSelect(`${detail.from.code} - ${display}`, 'from', display)
            }

            if (detail.to?.code) {
                const display = detail.to.display || detail.to.code
                searchManager.onAirportSelect(`${detail.to.code} - ${display}`, 'to', display)
            }

            // Bring the search form into view after prefill
            const scrollTarget =
                document.querySelector('[data-home-search-form]') || document.querySelector('[data-search-container]')

            if (scrollTarget && 'scrollIntoView' in scrollTarget) {
                requestAnimationFrame(() => {
                    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
                })
            }
        }

        window.addEventListener('home-search-prefill', listener as EventListener)
        return () => window.removeEventListener('home-search-prefill', listener as EventListener)
    }, [searchManager])

    // Override the search behavior for home page
    const performHomePageSearch = () => {
        const queryParams = new URLSearchParams()
        queryParams.set('from', searchManager.fromToInputsState.from.airportCode as string)
        queryParams.set('to', searchManager.fromToInputsState.to.airportCode as string)
        queryParams.set('adults', searchManager.travelersState.adults.toString())
        queryParams.set('childs', searchManager.travelersState.childs.toString())
        queryParams.set('infants', searchManager.travelersState.infants.toString())
        queryParams.set('class', searchManager.flightClassState as string)
        queryParams.set('depart_date', getFormattedDate(searchManager.range.from, 'DD/MM/YYYY'))
        queryParams.set(
            'return_date',
            searchManager.isRoundTrip ? getFormattedDate(searchManager.range.to, 'DD/MM/YYYY') : ''
        )
        queryParams.set('origin', searchManager.fromToInputsState.from.value as string)
        queryParams.set('destination', searchManager.fromToInputsState.to.value as string)

        // Add non-stop parameter if enabled
        if (searchManager.isNonStop) {
            queryParams.set('nonstop', '0')
        }

        // Note: Recent search is now saved automatically in SRP controller via useSrpController
        // No need to call updateHomeSearch here anymore

        const queryString = queryParams.toString()
        const url = `/${locale}/flights/results?${queryString}`
        router.push(url)
    }

    /**
     * Performs multi-city search and navigates to results page
     * Validates all segments have required data before proceeding
     */
    const performMultiCitySearch = () => {
        const segments = searchManager.multiCitySegments

        // Note: Validation is handled in the component (empty fields and same airport checks)
        // This function assumes validation has already passed

        const queryParams = new URLSearchParams()

        // Set trip type - always 3 for multi-city (1=One Way, 2=Round Trip, 3=Multi City)
        queryParams.set('tripType', '3')

        // Map segments to dep/arr/depdate parameters (1-3)
        segments.forEach((segment, index) => {
            const segmentNum = index + 1
            queryParams.set(`dep${segmentNum}`, segment.from.airportCode || '')
            queryParams.set(`arr${segmentNum}`, segment.to.airportCode || '')
            queryParams.set(`depdate${segmentNum}`, segment.date ? getFormattedDate(segment.date, 'YYYY-MM-DD') : '')
        })

        // Set empty values for missing segments (if only 2 segments, set segment 3 as empty)
        // Always set segments 3-6 as empty if they don't exist
        for (let i = segments.length + 1; i <= maxSegments; i++) {
            queryParams.set(`dep${i}`, '')
            queryParams.set(`arr${i}`, '')
            queryParams.set(`depdate${i}`, '')
        }

        // Map travelers (adt, chd, inf)
        queryParams.set('adt', searchManager.travelersState.adults.toString())
        queryParams.set('chd', searchManager.travelersState.childs.toString())
        queryParams.set('inf', searchManager.travelersState.infants.toString())

        // Map cabin class (cbn) - convert to single lowercase character
        const cabinClass = searchManager.flightClassState as string
        const cabinMap: Record<string, string> = {
            Economy: 'e',
            Business: 'b',
            First: 'f'
        }
        const cbn = cabinMap[cabinClass] || cabinClass.charAt(0).toLowerCase()
        queryParams.set('cbn', cbn)

        // Set additional parameters
        queryParams.set('airlineType', '')
        queryParams.set('isDirectFlights', 'false')

        // Add non-stop parameter if enabled
        if (searchManager.isNonStop) {
            queryParams.set('nonstop', '0')
        }

        // Add token parameter for 2-segment searches
        if (segments.length === 2) {
            queryParams.set('token', randomString())
        }

        const queryString = queryParams.toString()
        const url = `/flight/result?${queryString}`
        router.push(url)
    }

    // Return all existing functionality plus the home page specific search
    return {
        ...searchManager,
        performHomePageSearch,
        performMultiCitySearch
    }
}
